import styled, { css } from 'styled-components';
import Colors from 'styles/colors';

const headerSection = css`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 20px;
`;

export const Header = styled.header`
  background-color: ${Colors.bigStone};
  height: 56px;
  overflow: hidden;
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.6);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  ${(props) => props.env === 'stage' && props.site !== 'demo.continuum.epublishing.com' && css`
    background-color: #fde8e8;
    border-bottom: 3px solid #c0392b;
    color: #5a2d2d;

    .fa-search path {
      fill: #5a2d2d;
    }
  `}

  .fa-search {
    height: 18px;
    width: 18px;

    path {
      fill: rgba(255, 255, 255, 0.5);
    }
  }
`;

export const StagingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  height: 100%;
  background: #c0392b;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  flex-shrink: 0;
  user-select: none;

  .svg-inline--fa {
    width: 14px;
    height: 14px;
    path { fill: #fff; }
  }
`;

export const Search = styled.div`
  ${headerSection}
  flex: 1;
  gap: 10px;
  padding: 0 16px;

  input {
    border: none;
    padding: 8px 0;
    width: 100%;
    background-color: transparent;
    outline: none;
    color: #fff;
    font-size: 14px;

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    .staging-mode & {
      color: #3b1515;

      &::placeholder {
        color: #a06060;
      }
    }
  }
`;

export const Environment = styled.div`
  ${headerSection}
  padding: 0 12px;
`;

export const EnvLinks = styled.div`
  display: flex;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 2px;
  gap: 2px;

  .staging-mode & {
    background-color: ${Colors.cadetBlue};
  }
`;

export const EnvLink = styled.div`
  cursor: pointer;
  display: inline-block;
  line-height: 1;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-radius: 3px;
  transition: all 200ms ease;
  color: rgba(255, 255, 255, 0.85);

  .staging-mode & {
    color: ${Colors.bigStone};
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  ${(props) => props.active && !props.isStaging && css`
    background-color: ${Colors.curiousBlue};
    color: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

    &:hover {
      background-color: ${Colors.curiousBlue};
    }

    .staging-mode & {
      background-color: #fff;
      color: ${Colors.bigStone};
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

      &:hover {
        background-color: #fff;
      }
    }
  `}

  ${(props) => props.active && props.isStaging && css`
    background-color: #c0392b;
    color: #fff;
    box-shadow: 0 1px 3px rgba(192, 57, 43, 0.3);

    &:hover {
      background-color: #c0392b;
    }
  `}
`;

export const CacheButton = styled.div`
  margin-left: auto;
  padding: 0 8px;

  button {
    color: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.2);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    line-height: 1;
    padding: 6px 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-radius: 3px;
    transition: all 200ms ease;
    background-color: transparent;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.4);
      color: #fff;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    .staging-mode & {
      color: #5a2d2d;
      border-color: #d4a0a0;

      &:hover {
        background-color: rgba(192, 57, 43, 0.08);
        border-color: #5a2d2d;
      }
    }
  }
`;

export const Divider = styled.div`
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.12);
  flex-shrink: 0;

  .staging-mode & {
    background: #d4a0a0;
  }
`;

export const HelpLinks = styled.div`
  ${headerSection}
  padding: 0 8px;
  gap: 4px;
`;

export const HelpLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 200ms ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .svg-inline--fa {
    width: 16px;
    height: 16px;

    path {
      fill: rgba(255, 255, 255, 0.5);
    }
  }

  &:hover .svg-inline--fa path {
    fill: rgba(255, 255, 255, 0.8);
  }

  .staging-mode & .svg-inline--fa path {
    fill: #5a2d2d;
  }

  a {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const User = styled.div`
  ${headerSection}
  padding: 0 20px;
  justify-self: end;
  display: flex;
  align-items: center;

  .signout-link {
    border: none;
    padding: 0;
    background-color: transparent;
    color: ${Colors.pictonBlue};
    font-size: 13px;
    cursor: pointer;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
      color: #6ec0ed;
    }

    .staging-mode & {
      color: ${Colors.curiousBlue};
    }
  }
`;

export const UserName = styled.span`
  color: #fff;
  margin-right: 10px;
  font-size: 13px;
  font-weight: 500;

  .staging-mode & {
    color: #3b1515;
  }
`;

export const Photo = styled.div`
  display: inline-block;
  align-items: center;
  position: relative;
  cursor: pointer;
`;

export const Image = styled.div`
  overflow: hidden;
  height: 40px;
  width: 40px;
  border-radius: 20px;
  background-color: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;

  .svg-inline--fa {
    width: 20px;
    height: 20px;
    margin: 0 auto;

    path {
      fill: #fff;
    }
  }
`;

export const UserMenuIcon = styled.div`
  position: absolute;
  bottom: -3px;
  right: -1px;
  width: 16px;
  height: 16px;
  border-radius: 8px;
  background-color: ${Colors.chateauGreen};
  color: #fff;
  display: flex;
  align-items: center;

  .svg-inline--fa {
    width: 16px;
    height: 16px;
    margin: 0 auto;

    path {
      fill: #fff;
    }
  }
`;

export const CacheDropdownContent = styled.div`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  width: 440px;
  padding: 0;
  overflow: hidden;

  /* Cap the dropdown to the viewport so it doesn't clip below the fold on
     short screens. The 80px budget covers the header (56px) plus breathing
     room above the bottom edge. The dropdown becomes the single scroll
     surface; inner sections keep their own soft max-heights so no one
     section dominates when the user does need to scroll. */
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  overscroll-behavior: contain;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #bfbfbf;
  }
`;

/**
 * Each "scope tier" (One Page / Many Pages / Everything) gets its own band.
 * Tinted backgrounds group sub-sections without needing extra borders or
 * dividers — the whole dropdown looks structured rather than listy.
 */
export const CacheTier = styled.div`
  padding: 14px 0 12px;
  background: ${(props) => (props.$tone === 'secondary' ? '#f7f9fc' : '#ffffff')};

  & + & {
    border-top: 1px solid rgba(15, 23, 42, 0.04);
  }

  @media (max-height: 800px) {
    padding: 10px 0 8px;
  }
`;

export const CacheDropdownDivider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 8px 0;
`;

/**
 * The dominant rhythm — "ONE PAGE" / "MANY PAGES". Strong, dark, with a
 * colored accent bar so the eye locks onto the scope before the eye reads
 * the sub-section labels.
 */
export const CacheTierLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #1f2a37;

  &::before {
    content: '';
    width: 3px;
    height: 12px;
    background: ${Colors.curiousBlue};
    border-radius: 2px;
  }

  @media (max-height: 800px) {
    padding: 0 16px 6px;
  }
`;

/**
 * Sub-section caption inside a tier ("Recently edited", "Quick clears",
 * "By URL pattern"). Quieter than the tier label so the rhythm reads
 * tier → group → action instead of a flat list of equal headings.
 */
export const CacheSubsectionLabel = styled.div`
  padding: 4px 16px 4px 22px;
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;

  @media (max-height: 800px) {
    padding: 2px 16px 3px 22px;
    font-size: 10px;
  }
`;

/* Kept for backward import compatibility — same look as CacheSubsectionLabel. */
export const CacheSectionHeader = CacheSubsectionLabel;

/**
 * Both single-page actions in tier 1 — Clear Current Page and Homepage —
 * use this same component. Side-by-side they form a "single page scope"
 * pair, with the page-context action (Clear Current Page) staying solid
 * primary blue and Homepage downgrading to a $secondary outlined treatment
 * so the eye sees ranked priority instead of two equal blue buttons.
 *
 * When Homepage is alone (no current page context), it promotes back to
 * solid blue automatically — the consumer just doesn't pass $secondary.
 */
export const CacheOnePageButton = styled.button`
  display: block;
  width: 100%;
  padding: 11px 14px;
  border: 0;
  border-radius: 5px;
  background: ${Colors.curiousBlue};
  color: #fff;
  text-align: left;
  cursor: pointer;
  transition: background-color 150ms ease, transform 150ms ease,
    box-shadow 150ms ease;
  box-shadow: 0 1px 3px rgba(24, 144, 255, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);

  &:hover {
    background: #0c7cd5;
    box-shadow: 0 3px 10px rgba(24, 144, 255, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.12);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${Colors.curiousBlue};
    outline-offset: 2px;
  }

  strong {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 2px;
  }

  span {
    display: block;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.85);

    /* Long page labels can blow out a half-width button — ellipsis keeps
       the pair visually balanced. */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${(props) => props.$secondary && css`
    background: #ffffff;
    box-shadow: inset 0 0 0 1.5px ${Colors.curiousBlue};

    strong { color: ${Colors.curiousBlue}; }
    span   { color: #5b7a96; }

    &:hover {
      background: rgba(24, 144, 255, 0.06);
      box-shadow: inset 0 0 0 1.5px #0c7cd5,
        0 2px 6px rgba(24, 144, 255, 0.12);
      transform: translateY(-1px);
    }
  `}
`;

/** Kept as an alias for backward import compatibility. */
export const CacheCurrentPageButton = CacheOnePageButton;

export const CacheOnePageRow = styled.div`
  display: grid;
  /* auto-fit lets the surviving button take the full width when only one
     of the pair renders (e.g., when there's no current page context). */
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 6px;
  padding: 0 8px 6px;
`;

export const CacheQuickClearsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 0 8px 4px;

  /* Tighter chip rhythm on short laptops; the chips themselves stay legible
     because their padding is reduced rather than their type size. */
  @media (max-height: 800px) {
    gap: 4px;
  }
`;

export const CacheQuickClearChip = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  border-radius: 5px;
  background: #ffffff;
  padding: 8px 10px;
  cursor: pointer;
  transition: background-color 150ms ease, transform 150ms ease,
    box-shadow 150ms ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);

  &:hover {
    background: #ffffff;
    box-shadow: 0 3px 10px rgba(24, 144, 255, 0.18),
      0 0 0 1px rgba(24, 144, 255, 0.4);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${Colors.curiousBlue};
    outline-offset: 2px;
  }

  strong {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 2px;
  }

  span {
    display: block;
    font-size: 11px;
    color: #6b7280;
    line-height: 1.35;
  }

  ${(props) => props.$featured && css`
    background: ${Colors.curiousBlue};
    box-shadow: 0 1px 3px rgba(24, 144, 255, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.12);

    strong { color: #fff; font-size: 13px; }
    span   { color: rgba(255, 255, 255, 0.85); }

    &:hover {
      background: #0c7cd5;
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.12);
    }
  `}

  @media (max-height: 800px) {
    padding: 6px 8px;

    strong { font-size: 12px; }
    span   { font-size: 10px; }
  }
`;

export const CacheRecentEmpty = styled.div`
  padding: 6px 16px 10px;
  font-size: 11px;
  color: #8c8c8c;
  font-style: italic;
`;

export const CacheRecentList = styled.div`
  padding: 0 8px 6px;
  max-height: 200px;
  overflow-y: auto;

  /* Shrink further on short laptops so the dropdown's other sections stay
     visible without forcing the outer scroll. */
  @media (max-height: 800px) {
    max-height: 140px;
  }
  @media (max-height: 680px) {
    max-height: 100px;
  }

  /* Subtle scrollbar so the list doesn't fight the dropdown chrome. */
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 3px;
  }
`;

export const CacheRecentRow = styled.button`
  display: grid;
  grid-template-columns: 88px 1fr auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  text-align: left;
  transition: background-color 120ms ease, border-color 120ms ease;

  & + & { margin-top: 2px; }

  &:hover:not(:disabled) {
    background: #f5f9ff;
    border-color: rgba(24, 144, 255, 0.25);
  }

  &:focus-visible {
    outline: 2px solid ${Colors.curiousBlue};
    outline-offset: 1px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

/**
 * Per-kind tint so editors can scan the recent list visually before reading
 * titles. Subtle only — saturation kept low so no badge dominates the row.
 */
const KIND_TINTS = {
  Article: { bg: '#eaf2fa', fg: '#2e5c8a' },
  Page: { bg: '#eef3f8', fg: '#5b7a96' },
  Event: { bg: '#fcf2e6', fg: '#92541a' },
  Product: { bg: '#ecf5e8', fg: '#3f6f3a' },
  NewsletterIssue: { bg: '#f3eaf7', fg: '#6e3f8a' },
};

export const CacheRecentKind = styled.span`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  border-radius: 3px;
  padding: 3px 6px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
  ${(props) => {
    const tint = KIND_TINTS[props.$kind] || KIND_TINTS.Page;
    return css`
      color: ${tint.fg};
      background: ${tint.bg};
    `;
  }}
`;

export const CacheRecentSkeletonWrap = styled.div`
  padding: 0 8px 6px;

  /* react-content-loader's inline SVG inherits flow; keeping the wrapper
     padding identical to CacheRecentList prevents a layout jump when the
     real rows arrive. */
`;

export const CacheRecentTitle = styled.span`
  font-size: 12px;
  color: #262626;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CacheRecentTime = styled.span`
  font-size: 10px;
  color: #8c8c8c;
  white-space: nowrap;
`;

export const CachePatternForm = styled.div`
  padding: 4px 16px 10px;
`;

export const CachePatternInputRow = styled.div`
  display: flex;
  gap: 6px;

  input {
    flex: 1;
    border: 1px solid #d9d9d9;
    border-radius: 3px;
    padding: 6px 10px;
    font-size: 12px;
    outline: none;

    &::placeholder {
      color: #b0b3b8;
    }

    &:focus {
      border-color: ${Colors.curiousBlue};
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
    }
  }

  button {
    border: 1px solid ${Colors.curiousBlue};
    background: ${Colors.curiousBlue};
    color: #fff;
    border-radius: 3px;
    padding: 5px 14px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: #0c7cd5;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

export const CacheExamplesToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 8px 0 2px;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 500;
  color: ${Colors.curiousBlue};
  cursor: pointer;

  &:hover {
    color: #0c7cd5;
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${Colors.curiousBlue};
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

export const CacheExamplesCaret = styled.span`
  display: inline-flex;
  align-items: center;
  transition: transform 150ms ease;
  transform: rotate(${(props) => (props.$open ? '90deg' : '0deg')});

  .svg-inline--fa {
    width: 9px;
    height: 9px;
    path { fill: currentColor; }
  }
`;

export const CacheExamplesPanel = styled.div`
  margin-top: 6px;
  padding: 8px;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.04);
`;

export const CacheExamplesIntro = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 6px;
  line-height: 1.4;
`;

export const CacheExampleRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  padding: 6px 8px;
  margin: 0;
  background: #f7f9fc;
  border: 0;
  border-radius: 3px;
  cursor: pointer;
  text-align: left;
  transition: background-color 150ms ease, box-shadow 150ms ease;

  & + & {
    margin-top: 4px;
  }

  &:hover {
    background: #eaf2fa;
    box-shadow: 0 0 0 1px rgba(24, 144, 255, 0.3);
  }

  &:focus-visible {
    outline: 2px solid ${Colors.curiousBlue};
    outline-offset: 1px;
  }
`;

export const CacheExampleLabel = styled.span`
  font-size: 12px;
  color: #262626;
  flex: 1;
`;

export const CacheExamplePattern = styled.code`
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 11px;
  color: #6b7280;
  background: #f0f2f5;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

/**
 * Intentionally quiet — product goal is to discourage full-site clears, so
 * the affordance reads as a small subdued footer link rather than an action
 * button. The destructive confirmation panel handles emphasis once the user
 * has actually committed to opening it.
 */
export const CacheClearAllLink = styled.button`
  display: block;
  width: 100%;
  padding: 11px 16px;
  background: transparent;
  border: 0;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  font-size: 11px;
  color: #94a3b8;
  text-align: center;
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;

  &:hover {
    background: #fff7ed;
    color: #b45309;
  }

  &:focus-visible {
    outline: 2px solid #c2410c;
    outline-offset: -2px;
  }
`;

/* Kept exported for backward import compatibility while the dropdown is
   migrated. Not used by the new tier layout. */
export const CacheClearAllButton = CacheClearAllLink;

export const CacheCaret = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;

  .svg-inline--fa {
    width: 10px;
    height: 10px;
  }
`;

export const CacheConfirmPanel = styled.div`
  padding: 14px 16px 12px;
  background: #fff7ed;
  border-top: 1px solid #fed7aa;
  border-bottom: 1px solid #fed7aa;
`;

export const CacheConfirmTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #9a3412;
  margin-bottom: 6px;

  .svg-inline--fa {
    width: 14px;
    height: 14px;
    path { fill: #c2410c; }
  }
`;

export const CacheConfirmBody = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: #7c2d12;
  margin-bottom: 10px;

  strong {
    font-weight: 700;
    color: #9a3412;
  }
`;

export const CacheConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  button {
    border-radius: 3px;
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid #d9d9d9;
    background: #fff;
    color: #595959;

    &:hover {
      background: #fafafa;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.danger {
      border-color: #c2410c;
      background: #c2410c;
      color: #fff;

      &:hover {
        background: #9a3412;
      }
    }
  }
`;

export const WarningText = styled.div`
`;

export const HeaderLinks = styled.ul`
  list-style-type: none;
  color: rgba(255, 255, 255, 0.8);
  padding: 0;
  margin: 0;

  span {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.45);
  }

  .staging-mode & {
    color: #3b1515;

    span {
      color: #7a4a4a;
    }
  }
`;
